import { useContext, useState } from "react";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { LanguageNameSpaces, OpenMessage } from "@shared/types";
import { round } from "lodash";
import { getPairFromSymbol } from "@shared/utils/misc";
import { useSummaryAmount } from "@shared/features/tradingTerminal/common/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useSummary = (openMessage: OpenMessage, isOrder?: boolean) => {
  const {
    symbols,
    amount,
    quantity,
    quantityAmount,
    amountCoin,
    quantityCoin,
    numErrors,
    numErrorsMessage,
    isMobile,
    convertMainCoinAmountToUSD,
    isLoading,
    precisionConfig,
    availableAmount,
    isSignal,
  } = useContext(TradingTerminalContext)!;

  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);

  const [freeTextModalOpen, setFreeTextModalOpen] = useState(false);
  const [ordersSummaryModalOpen, setOrdersSummaryModalOpen] = useState(false);
  const [chartDrawerOpen, setChartDrawerOpen] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const formattedAmount = useSummaryAmount();

  const iconTooltipTitle = isMobile
    ? t("labels.trade_chart")
    : t("tooltips.trade_summary");
  const actionButtonTitle = isMobile
    ? t("buttons.review")
    : t(
        isOrder
          ? "buttons.create_order"
          : isSignal
          ? "buttons.create_signal"
          : "buttons.create_trade"
      );
  const actionButtonDisabled = !isMobile && numErrors > 0;
  const usdAvailableAmount =
    convertMainCoinAmountToUSD?.(availableAmount, true) || 0;
  const usdAmount = convertMainCoinAmountToUSD?.(Number(amount), true) || 0;
  const usdQuantityAmount = convertMainCoinAmountToUSD?.(
    Number(quantityAmount),
    true
  );
  const displayedAmountCoin = amountCoin ?? getPairFromSymbol(symbols[0] || "");
  const availableAmountData = {
    shouldDisplay: !isSignal,
    title: t("labels.available_amount"),
    value: `${round(availableAmount, precisionConfig?.amount)} ${
      displayedAmountCoin || ""
    }`,
    usdValue: `(${usdAvailableAmount} USD)`,
  };

  const tradeAmountData = {
    title: isSignal ? t("labels.amount") : t("labels.trade_amount"),
    value: formattedAmount,
    usdValue: isSignal ? "" : `(${usdAmount} USD)`,
  };

  const existingAmountData = {
    title: t("labels.existing_amount"),
    value: `${quantity} ${quantityCoin}`,
    usdValue: `(${usdQuantityAmount} USD)`,
  };
  const showExistingAmount = parseFloat(quantity || "") > 0;

  const onActionButtonClick = () => {
    if (actionButtonDisabled) {
      openMessage(t("messages.errors.resolve_errors"), "warning");
    } else {
      setIsConfirm(true);
      setOrdersSummaryModalOpen(true);
    }
  };

  const onIconButtonClick = () => {
    if (isMobile) {
      setChartDrawerOpen(true);
    } else {
      setOrdersSummaryModalOpen(true);
    }
  };

  const closeSummaryModal = () => {
    setIsConfirm(false);
    setOrdersSummaryModalOpen(false);
  };

  return {
    actionButtonTitle,
    onActionButtonClick,
    actionButtonDisabled,
    ordersSummaryModalOpen,
    isConfirm,
    onIconButtonClick,
    freeTextModalOpen,
    setFreeTextModalOpen,
    isMobile,
    chartDrawerOpen,
    setChartDrawerOpen,
    iconTooltipTitle,
    closeSummaryModal,
    isLoading,
    availableAmountData,
    tradeAmountData,
    existingAmountData,
    numErrors,
    numErrorsMessage,
    showExistingAmount,
  };
};

export default useSummary;
